import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, Mail, User, UserCog } from 'lucide-react';

const ApprovalActionCTA = ({ 
  approverType, 
  approverName, 
  approverEmail, 
  approverData, // Full approver object with delegate info
  onApprovalAction, 
  currentUser,
  onResendNotification 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [action, setAction] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  // Check if there's a delegate
  const hasDelegate = approverData?.delegate;
  const delegateName = hasDelegate ? approverData.delegate.user_full_name : null;
  const delegateEmail = hasDelegate ? approverData.delegate.user_email : null;
  
  // Debug: Log the comparison

  // Check if current user is the approver OR the delegate
  const isCurrentUser = String(currentUser?.user_email || '').toLowerCase().trim() === String(approverEmail || '').toLowerCase().trim();
  const isDelegate = hasDelegate && String(currentUser?.user_email || '').toLowerCase().trim() === String(delegateEmail || '').toLowerCase().trim();
  const canApprove = isCurrentUser || isDelegate;

  const handleAction = async (actionType) => {
    setSubmitting(true);
    try {
      await onApprovalAction(actionType, approverType, notes);
      setAction('');
      setNotes('');
      setIsExpanded(false);
    } finally {
      setSubmitting(false);
    }
  };

  const getApproverTitle = (type) => {
    switch (type) {
      case 'manager': return 'Manager/Supervisor';
      case 'hod': return 'Department HOD';
      case 'it_hod': return 'IT HOD';
      case 'director': return 'Managing Director';
      default: return 'Approver';
    }
  };

  const getApproverIcon = (type) => {
    switch (type) {
      case 'manager': return <User className="w-5 h-5" />;
      case 'hod': return <User className="w-5 h-5" />;
      case 'it_hod': return <User className="w-5 h-5" />;
      case 'director': return <User className="w-5 h-5" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border-2 border-blue-500">
      {/* Approver Info Header - Always show */}
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              {getApproverIcon(approverType)}
              <span className="font-medium text-gray-900">
                {getApproverTitle(approverType)}
              </span>
            </div>
            <div className="ml-7 space-y-1">
              <div className="text-sm">
                <span className="text-gray-600">Approver: </span>
                <span className="font-medium text-gray-900">{approverName}</span>
              </div>
              <div className="text-xs text-gray-500">{approverEmail}</div>
              
              {/* Show delegate info if exists */}
              {hasDelegate && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center space-x-1 text-xs text-blue-700 mb-1">
                    <UserCog className="w-3 h-3" />
                    <span className="font-medium">Delegated to:</span>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{delegateName}</div>
                    <div className="text-xs text-gray-500">{delegateEmail}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      {canApprove ? (
        <div className="p-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" />
              <span className="text-blue-800 text-sm font-medium">
                {isDelegate 
                  ? `You are acting as delegate for ${approverName}` 
                  : 'Your approval is requested for this request'}
              </span>
            </div>
          </div>

          {/* Comments Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comments (Optional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any comments or conditions for your approval..."
            />
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleAction('approved')}
              disabled={submitting}
              className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Approve
            </button>
            
            <button
              onClick={() => handleAction('rejected')}
              disabled={submitting}
              className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Reject
            </button>
            
            <button
              onClick={() => handleAction('more_info')}
              disabled={submitting}
              className="px-3 py-1.5 bg-yellow-600 text-white text-sm font-medium rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              More Info
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4">
          <div className="text-center mb-4">
            <div className="flex items-center justify-center mb-2">
              <Mail className="w-5 h-5 text-gray-400 mr-2" />
              <span className="text-gray-600">
                Waiting for {hasDelegate ? delegateName : approverName} to take action
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {hasDelegate 
                ? `${delegateName} (acting as delegate for ${approverName}) will receive a notification to review and approve this request.`
                : `${approverName} will receive a notification to review and approve this request.`}
            </p>
          </div>
          {onResendNotification && (
            <div className="flex justify-center">
              <button
                onClick={async () => {
                  setResending(true);
                  try {
                    await onResendNotification(approverType);
                  } finally {
                    setResending(false);
                  }
                }}
                disabled={resending}
                className="px-4 py-2 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {resending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Approval Request
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApprovalActionCTA;
